import { useTranslation } from '@pos/i18n';
import { parseRichText, type Inline, type RichBlock } from '@pos/rich-text';
import type { CSSProperties, ReactNode } from 'react';
import { COLOUR, EMAIL_COLOURS, fill, safeImage, safeLink, type EmailLayout } from './emailModel';

/*
 * The email's own look, mirroring the server's renderer (EmailRenderer.cs, ADR 0125): a 600 pixel white card on the brand's
 * background, the logo, the text, then the footer. These inline styles are the email's, not HO admin's: an email has no Carbon
 * theme, so the preview keeps the email's fixed colours and sizes whatever HO admin's theme is.
 */
const FONT = 'Arial, Helvetica, sans-serif';
const HEADING_SIZE = { 2: 22, 3: 18, 4: 16 } as const;
const cell: CSSProperties = { padding: '6px 8px', border: `1px solid ${EMAIL_COLOURS.rule}`, textAlign: 'left', verticalAlign: 'top' };

type Values = Readonly<Record<string, string>>;

/**
 * How an email will look (ADR 0125), drawn with React elements from the template's rich text (`parseRichText` with email links):
 * the text is read first and the values are filled into its words afterwards, so a value (or anything typed) is always shown as
 * text, never as HTML or formatting. A link or picture whose address isn't safe once filled in is left as its words, as the
 * server does. A marketing email ends with the unsubscribe line the server always adds.
 */
export function EmailPreview({
  layout,
  subject,
  body,
  values,
  marketing,
}: {
  layout: EmailLayout;
  subject?: string;
  body: string;
  values: Values;
  marketing?: boolean;
}) {
  const { t } = useTranslation();
  const accent = COLOUR.test(layout.accentColor) ? layout.accentColor : EMAIL_COLOURS.accent;
  const background = COLOUR.test(layout.backgroundColor) ? layout.backgroundColor : EMAIL_COLOURS.background;
  const logo = layout.logoUrl ? safeImage(layout.logoUrl) : null;
  const links = layout.links.filter((l) => l.label.trim() && safeLink(l.url));
  const footer = layout.footer.trim();
  const title = subject === undefined ? undefined : fill(subject, values).trim();
  const blocks = parseRichText(body, { links: 'email' });

  return (
    <figure className="pos-email-preview" aria-label={t('messaging.email.preview.title')}>
      {title !== undefined && (
        <figcaption className="pos-email-preview__subject">
          {t('messaging.email.subject')}: <strong>{title || '—'}</strong>
        </figcaption>
      )}
      <div style={{ background, padding: '24px 12px' }}>
        <div style={{ maxWidth: 600, margin: '0 auto', background: EMAIL_COLOURS.card, fontFamily: FONT, color: EMAIL_COLOURS.ink }}>
          {logo && (
            <div style={{ padding: '24px 32px 8px' }}>
              <img src={logo} alt="" height={48} style={{ display: 'block', border: 0, height: 48, width: 'auto' }} />
            </div>
          )}
          {blocks.length > 0 && (
            <div style={{ padding: '8px 32px 24px', fontSize: 15, lineHeight: 1.6 }}>
              {blocks.map((block, i) => (
                <PreviewBlock key={i} block={block} values={values} accent={accent} />
              ))}
            </div>
          )}
          {(footer || links.length > 0 || marketing) && (
            <div style={{ padding: '24px 32px', background: EMAIL_COLOURS.footer, fontSize: 12, lineHeight: 1.5, color: EMAIL_COLOURS.muted }}>
              {footer && <p style={{ margin: '0 0 8px', whiteSpace: 'pre-line' }}>{footer}</p>}
              {links.length > 0 && (
                <p style={{ margin: '0 0 8px' }}>
                  {links.map((l, k) => (
                    <span key={k}>
                      {k > 0 && ' · '}
                      <span style={{ color: accent, textDecoration: 'underline' }}>{l.label.trim()}</span>
                    </span>
                  ))}
                </p>
              )}
              {marketing && <p style={{ margin: 0, textDecoration: 'underline' }}>{t('messaging.email.unsubscribe')}</p>}
            </div>
          )}
        </div>
      </div>
    </figure>
  );
}

function PreviewBlock({ block, values, accent }: { block: RichBlock; values: Values; accent: string }) {
  const words = (items: Inline[]) => <Words items={items} values={values} accent={accent} />;
  switch (block.kind) {
    case 'heading': {
      const Tag = `h${block.level}` as const;
      return <Tag style={{ margin: '16px 0 8px', fontSize: HEADING_SIZE[block.level], lineHeight: 1.3, fontWeight: 700 }}>{words(block.children)}</Tag>;
    }
    case 'paragraph':
      return <p style={{ margin: '0 0 12px' }}>{words(block.children)}</p>;
    case 'list': {
      const List = block.ordered ? 'ol' : 'ul';
      return (
        <List style={{ margin: '0 0 12px', paddingLeft: 24, listStyle: block.ordered ? 'decimal' : 'disc' }}>
          {block.items.map((item, i) => (
            <li key={i} style={{ margin: '0 0 4px' }}>
              {words(item)}
            </li>
          ))}
        </List>
      );
    }
    case 'quote':
      return (
        <blockquote style={{ margin: '0 0 12px', padding: '4px 0 4px 12px', borderLeft: `4px solid ${accent}`, color: EMAIL_COLOURS.muted }}>
          {block.paragraphs.map((p, i) => (
            <p key={i} style={{ margin: '0 0 8px' }}>
              {words(p)}
            </p>
          ))}
        </blockquote>
      );
    case 'divider':
      return <div style={{ borderTop: `1px solid ${EMAIL_COLOURS.rule}`, margin: '16px 0' }} />;
    case 'image':
      return (
        <img
          src={block.src}
          alt={fill(block.alt, values)}
          style={{ display: 'block', width: '100%', maxWidth: 536, height: 'auto', border: 0, margin: '0 0 12px' }}
        />
      );
    case 'table':
      return (
        <table style={{ width: '100%', borderCollapse: 'collapse', margin: '0 0 12px', fontSize: 14 }}>
          <thead>
            <tr>
              {block.header.map((c, i) => (
                <th key={i} style={{ ...cell, background: EMAIL_COLOURS.footer }}>
                  {words(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, r) => (
              <tr key={r}>
                {row.map((c, i) => (
                  <td key={i} style={cell}>
                    {words(c)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
  }
}

/** Inline words with the values filled into each piece of text (never re-read as formatting); a link shown, not followed. */
function Words({ items, values, accent }: { items: Inline[]; values: Values; accent: string }): ReactNode {
  return items.map((item, i) => {
    switch (item.kind) {
      case 'text':
        return <span key={i}>{fill(item.text, values)}</span>;
      case 'bold':
        return (
          <strong key={i}>
            <Words items={item.children} values={values} accent={accent} />
          </strong>
        );
      case 'italic':
        return (
          <em key={i}>
            <Words items={item.children} values={values} accent={accent} />
          </em>
        );
      case 'link': {
        const inner = <Words items={item.children} values={values} accent={accent} />;
        return safeLink(fill(item.href, values)) ? (
          <span key={i} style={{ color: accent, textDecoration: 'underline' }}>
            {inner}
          </span>
        ) : (
          <span key={i}>{inner}</span>
        );
      }
    }
  });
}
